import AssessmentIcon from '@mui/icons-material/Assessment';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

interface BottomNavProps {
  className?: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

/**
 * Altezza della bottom navigation bar in pixel.
 */
export const BOTTOM_NAV_HEIGHT = 64;

export default function BottomNav({ className }: BottomNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const bottomNavItems: NavItem[] = useMemo(
    () => [
      { label: t('nav.home'), path: '/', icon: <HomeIcon /> },
      { label: t('nav.goodsReceiving'), path: '/goods-receiving', icon: <LocalShippingIcon /> },
      { label: t('nav.autocontrollo'), path: '/haccp', icon: <AssessmentIcon /> },
    ],
    [t],
  );

  const currentIndex = useMemo(() => {
    const exactIndex = bottomNavItems.findIndex((item) => item.path === location.pathname);
    if (exactIndex >= 0) {
      return exactIndex;
    }

    const prefixIndex = bottomNavItems.findIndex(
      (item) => item.path !== '/' && location.pathname.startsWith(item.path),
    );
    return prefixIndex >= 0 ? prefixIndex : -1;
  }, [bottomNavItems, location.pathname]);

  return (
    <Box
      className={className}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        paddingBottom: 'var(--safe-area-bottom)',
        backgroundColor: '#1E293B',
        borderTop: '1px solid rgba(51, 65, 85, 0.4)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          height: BOTTOM_NAV_HEIGHT,
          px: 1,
        }}
      >
        {bottomNavItems.map((item, index) => {
          const isActive = currentIndex === index;
          return (
            <ButtonBase
              key={item.path}
              onClick={() => navigate(item.path)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                height: '100%',
                position: 'relative',
                borderRadius: 2,
                gap: '2px',
                WebkitTapHighlightColor: 'transparent',
                transition: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                // Pill background
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: isActive
                    ? 'translate(-50%, -50%) scale(1)'
                    : 'translate(-50%, -50%) scale(0.85)',
                  width: 56,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: isActive ? 'rgba(107, 70, 193, 0.2)' : 'transparent',
                  transition: 'background-color 0.25s ease, transform 0.25s ease',
                  zIndex: 0,
                },
                '&:active::before': {
                  transform: 'translate(-50%, -50%) scale(0.92)',
                  backgroundColor: 'rgba(107, 70, 193, 0.15)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': {
                    fontSize: isActive ? 24 : 22,
                    color: isActive ? '#FFFFFF' : '#94A3B8',
                    transition: 'color 0.25s ease, font-size 0.25s ease',
                  },
                }}
              >
                {item.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  fontSize: '0.68rem',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#FFFFFF' : '#94A3B8',
                  letterSpacing: isActive ? '0.02em' : '0',
                  transition: 'color 0.25s ease, font-weight 0.25s ease, letter-spacing 0.25s ease',
                  lineHeight: 1.2,
                  mt: '1px',
                }}
              >
                {item.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
